import * as coreClient from "@azure/core-client";
import * as Parameters from "./models/parameters";
import * as Mappers from "./models/mappers";
import {
  HostOptions,
  DiscountAPIOptionalParams,
  ApplyVoucherCodeOptionalParams,
  ApplyVoucherCodeResponse,
  RemoveVoucherCodeOptionalParams,
  RemoveVoucherCodeResponse,
  CustomerLoginNotifyDiscountOnBasketOptionalParams,
  CustomerLoginNotifyDiscountOnBasketResponse
} from "./models";

export class DiscountAPI extends coreClient.ServiceClient {
  $host: HostOptions;

  /**
   * Initializes a new instance of the DiscountAPI class.
   * @param options The parameter options
   */
  constructor(options?: DiscountAPIOptionalParams) {
    // Initializing default values for options
    if (!options) {
      options = {};
    }
    const defaults: DiscountAPIOptionalParams = {
      requestContentType: "application/json; charset=utf-8"
    };

    const packageDetails = `azsdk-js-discountAPI/1.0.0-beta.1`;
    const userAgentPrefix =
      options.userAgentOptions && options.userAgentOptions.userAgentPrefix
        ? `${options.userAgentOptions.userAgentPrefix} ${packageDetails}`
        : `${packageDetails}`;

    const optionsWithDefaults = {
      ...defaults,
      ...options,
      userAgentOptions: {
        userAgentPrefix
      },
      baseUri:
        options.endpoint ??
        options.baseUri ??
        "https://nxbus.api.nationalexpress.com"
    };
    super(optionsWithDefaults);

    // Assigning values to Constant parameters
    this.$host = options.$host || "https://nxbus.api.nationalexpress.com";
  }

  /**
   * Apply Voucher Code on the Basket
   * @param options The options parameters.
   */
  applyVoucherCode(
    options?: ApplyVoucherCodeOptionalParams
  ): Promise<ApplyVoucherCodeResponse> {
    return this.sendOperationRequest(
      { options },
      applyVoucherCodeOperationSpec
    );
  }

  /**
   * Remove Voucher Code on the Basket (Use Request content-type as application/json )
   * @param basketId Basket Id (UUID Unique for Each Basket)
   * @param options The options parameters.
   */
  removeVoucherCode(
    basketId: string,
    options?: RemoveVoucherCodeOptionalParams
  ): Promise<RemoveVoucherCodeResponse> {
    return this.sendOperationRequest(
      { basketId, options },
      removeVoucherCodeOperationSpec
    );
  }

  /**
   * Customer Login Notify For Discount on the Basket
   * @param basketId Basket Id (UUID Unique for Each Basket)
   * @param openIDToken The OpenID Token passed from Azure AD B2C
   * @param options The options parameters.
   */
  customerLoginNotifyDiscountOnBasket(
    basketId: string,
    openIDToken: string,
    options?: CustomerLoginNotifyDiscountOnBasketOptionalParams
  ): Promise<CustomerLoginNotifyDiscountOnBasketResponse> {
    return this.sendOperationRequest(
      { basketId, openIDToken, options },
      customerLoginNotifyDiscountOnBasketOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const applyVoucherCodeOperationSpec: coreClient.OperationSpec = {
  path: "/discount/V1/voucher-code/apply",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.VoucherCodeApplyResponse
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
const removeVoucherCodeOperationSpec: coreClient.OperationSpec = {
  path: "/discount/V1/voucher-code/remove",
  httpMethod: "DELETE",
  responses: {
    200: {
      bodyMapper: Mappers.VoucherCodeRemoveResponse
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept, Parameters.basketId],
  serializer
};
const customerLoginNotifyDiscountOnBasketOperationSpec: coreClient.OperationSpec = {
  path: "/discount/V1/customer-login/notify",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.CustomerLoginNotificationResponse
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host],
  headerParameters: [
    Parameters.accept,
    Parameters.basketId,
    Parameters.openIDToken
  ],
  serializer
};
